import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import uuid from 'react-uuid';
import styled, { css } from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import { CHART_GRAPH } from '@/entities/chart/model/constants';
import useChartStateAction from '@/features/(charts)/chart-controller/hooks/use-chart-state-action/useChartStateAction';
import DateRangeDisplay from '@/features/(charts)/chart-detail/ui/date-range-display/date-range-display';
import { useOpenChartClickDialog } from '@/features/(charts)/chart-renderer/hooks';
import { buildFeedbackPeakAnalysisOptions } from '@/features/(charts)/chart-renderer/lib/build-feedback-peak-analysis-options';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import {
  fieldToWordCloudBasis,
  isWordCloudDatasetField,
  isWordCloudGraph,
} from '@/features/(charts)/chart-renderer/lib/word-cloud-basis';
import useChartWordCloudItems from '@/features/(charts)/chart-renderer/ui/Single/use-chart-word-cloud-items';
import { TranslateLanguageSelect } from '@/features/WordCloud/components/TranslateLanguageSelect';
import { WordCloud } from '@/features/WordCloud/components/WordCloud';
import { useWordCloudAction } from '@/features/WordCloud/hooks/useWordCloudAction';
import useWordCloudFeedbackListModal from '@/features/WordCloud/hooks/useWordCloudFeedbackListModal';
import { useWordCloudTranslation } from '@/features/WordCloud/hooks/useWordCloudTranslation';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { radius } from '@/styles/sizes';
import { customScrollbar } from '@/styles/style';
import { getTimezoneDayjs } from '@/utils/time';

import ChartRenderer from './chart-renderer';

type Props = {
  chartInfo: ChartContextProps;
  yAxisTitle?: string;
};

const ChartChartArea = (props: Props) => {
  const openWordCloudFeedbackListModal = useWordCloudFeedbackListModal();
  const openChartClickDialog = useOpenChartClickDialog();

  const { chartInfo } = props;
  const { state, selectedField, tableQuery, originalChartInfo } = chartInfo;
  const tableData = tableQuery.data;
  const shouldRenderWordCloud = isWordCloudGraph(state.graph);
  const { onHistoricalChange } = useChartStateAction(props.chartInfo);
  const wordCloudData = useChartWordCloudItems({
    chart: {
      ...state,
      selected_field: selectedField,
    },
    tableData,
  });

  const {
    isSupported: isTranslationSupported,
    targetLanguage,
    setTargetLanguage,
    translatedWords,
    isTranslating,
  } = useWordCloudTranslation({
    words: wordCloudData,
    enabled: shouldRenderWordCloud && wordCloudData.length > 0,
  });

  const onWordClick = (word: { text: string; originalKeyword?: string }) => {
    const dateRange = chartUtils.getTimeRangeByTerm(
      getTimezoneDayjs(),
      state.target.term,
    );
    const keyword = word.originalKeyword || word.text;

    if (isWordCloudDatasetField(state.field.type)) {
      openWordCloudFeedbackListModal({
        keyword,
        translatedKeyword: word.text,
        basis: fieldToWordCloudBasis(state.field.type),
        peakAnalysis: buildFeedbackPeakAnalysisOptions({
          ...state,
          name: chartInfo.title,
        }),
        filter: {
          ...state.target.filter,
          start_date: dateRange[0].toJSON(),
          end_date: dateRange[1].toJSON(),
        },
      });
      return;
    }

    openChartClickDialog({
      alias: originalChartInfo?.alias || 'chart-wordcloud',
      type: DETAIL_MODAL_TYPE.CHART,
      options: {
        chart: {
          ...state,
          name: chartInfo.title,
          selected_field: selectedField,
          graph: CHART_GRAPH.BAR,
        },
        isAll: false,
        cell: {
          compared_target: false,
          series_index: 0,
          item_x: keyword,
          item_x_name: word.text,
        },
      },
    });
  };

  const { handleCopyToClipboard, handleDownloadPNG } = useWordCloudAction();
  const wordCloudId = useMemo(() => uuid(), []);
  const hasWordCloudData = translatedWords.length > 0;
  const isWordCloudLoading = tableQuery.isFetching || isTranslating;

  return (
    <Style>
      <ChartWrapper g={12}>
        <Transition
          status={true}
          css={css`
            display: flex;
            flex-direction: column;
            flex: 1;
            width: 100%;
            aspect-ratio: 2/1;
            min-height: 400px;
            overflow: hidden;
          `}
        >
          {tableData && !shouldRenderWordCloud && (
            <ChartRenderer
              tableData={tableData}
              chart={{
                ...state,
                name: chartInfo.title,
                selected_field: selectedField,
              }}
              onHistoricalChange={onHistoricalChange}
              yAxisTitle={props.yAxisTitle}
            />
          )}
          {shouldRenderWordCloud && (
            <WordCloud
              wordCloudId={wordCloudId}
              words={translatedWords}
              onWordClick={onWordClick}
              isLoading={isWordCloudLoading}
              basis={fieldToWordCloudBasis(state.field.type)}
            />
          )}
        </Transition>
        <Flex j="space-between" a="center">
          <DateRangeDisplay
            term={state.target.term}
            compareTerm={state.target.compare_term}
          />
          {hasWordCloudData && (
            <Flex g={8}>
              {isTranslationSupported && (
                <TranslateLanguageSelect
                  value={targetLanguage}
                  onChange={setTargetLanguage}
                  isLoading={isTranslating}
                />
              )}
              {shouldRenderWordCloud && (
                <>
                  <IconButton
                    icon={<DownloadOutlined />}
                    sizeType="m"
                    onClick={() => handleDownloadPNG(wordCloudId)}
                  />
                  <IconButton
                    icon={<CopyOutlined />}
                    sizeType="m"
                    onClick={() => handleCopyToClipboard(wordCloudId)}
                  />
                </>
              )}
            </Flex>
          )}
        </Flex>
      </ChartWrapper>
    </Style>
  );
};

const Style = styled(Flex)`
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: ${radius.card}px;
  padding: 16px;
`;

const ChartWrapper = styled(VFlex)`
  flex: 1;
  width: 100%;
  overflow: auto;
  position: relative;
  ${customScrollbar};
`;

export default ChartChartArea;
